import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, serviceSchema, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad är tv-produktion?",
    a: "Tv-produktion är film som produceras för sändning eller streaming — tv-reklam, sponsrade inslag, programvinjetter och längre format för playtjänster. Skillnaden mot webbfilm ligger i tekniska leveranskrav: sändningsformat, ljudnivåer enligt EBU R128, klockade längder och separata versioner per kanal.",
  },
  {
    q: "Vad kostar en tv-reklam?",
    a: "En tv-reklam på 15–30 sekunder kostar typiskt 150 000–450 000 kr i produktion. Enklare versionering av befintligt material till sändningsformat ligger på 15 000–40 000 kr. Mediakostnad för sändning tillkommer och köps separat av er eller er mediebyrå.",
  },
  {
    q: "Vilka tekniska krav gäller för tv?",
    a: "Sändande kanaler kräver oftast ProRes eller XDCAM i rätt upplösning och bildfrekvens, ljud normaliserat till −23 LUFS enligt EBU R128, exakta längder (10, 15, 20, 30 sek) och separat textning. Vi levererar färdigt enligt respektive kanals specifikation.",
  },
  {
    q: "Kan ni versionera en befintlig film för tv?",
    a: "Ja. Har ni redan en reklamfilm klipper vi ned den till sändningslängder, mixar om ljudet till rätt nivåer och exporterar i kanalens format. Det är den snabbaste vägen till tv om produktionen redan är gjord.",
  },
  {
    q: "Gör ni även program och längre format?",
    a: "Vi producerar sponsrade inslag, kortare programformat och seriellt innehåll för playtjänster och egna kanaler. Vi gör inte tv-drama eller spelfilm.",
  },
  {
    q: "Hur lång tid tar en tv-produktion?",
    a: "Räkna 4–7 veckor från brief till godkänt sändningsmaterial: koncept och manus 1–2 veckor, pre-production 1–2 veckor, inspelning 1–2 dagar och post inklusive versionering 2–3 veckor.",
  },
];

export const Route = createFileRoute("/tv-produktion")({
  head: () => ({
    meta: [
      { title: "Tv-produktion och tv-reklam — pris och process 2026 | Fenny" },
      {
        name: "description",
        content:
          "Tv-produktion för sändning och play: tv-reklam, sponsrade inslag och versionering till sändningsformat. Prisspann 2026, tekniska krav och tidsplan.",
      },
      { property: "og:title", content: "Tv-produktion och tv-reklam — pris och process" },
      {
        property: "og:description",
        content:
          "Vad en tv-reklam kostar, vilka sändningskrav som gäller och hur lång tid en tv-produktion tar.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/tv-produktion" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/tv-produktion" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/tv-produktion", name: "Tv-produktion och tv-reklam — pris och process 2026 | Fenny", description: "Tv-produktion för sändning och play: tv-reklam, sponsrade inslag och versionering till sändningsformat. Prisspann 2026, tekniska krav och tidsplan."})) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Tv-produktion och tv-reklam",
            path: "/tv-produktion",
            serviceType: "Tv-produktion",
            description:
              "Produktion av tv-reklam, sponsrade inslag och sändningsklart material enligt kanalernas tekniska specifikationer.",
            priceRange: "15 000–450 000 kr",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Tv-produktion", path: "/tv-produktion" },
          ]),
        ),
      },
    ],
  }),
  component: TvProduktionPage,
});

function TvProduktionPage() {
  return (
    <LandingPage
      eyebrow="Tv-produktion"
      title={<>Tv-produktion som klarar sändningskraven första gången.</>}
      lede="Fenny producerar tv-reklam, sponsrade inslag och sändningsklart material för linjär tv och playtjänster. Vi levererar i kanalens format, med ljud enligt EBU R128 och exakta längder — så materialet godkänns utan omtag."
      tldr={
        <p>
          Tv-produktion är film gjord för sändning: rätt format, rätt ljudnivå
          och klockade längder. Pris: <strong>150 000–450 000 kr</strong> för en
          tv-reklam 15–30 sek, <strong>15 000–40 000 kr</strong> för att
          versionera befintligt material till sändningsformat. Mediaköp
          tillkommer.
        </p>
      }
      ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
      ctaSecondary={{ label: "Se case", to: "/case" }}
      trackingId="tv-produktion"
      bullets={[
        {
          title: "Sändningsklart",
          body: "ProRes/XDCAM, −23 LUFS, klockade längder och textning enligt kanalens spec.",
        },
        {
          title: "En produktion, alla ytor",
          body: "Samma inspelning ger tv-version, sociala format och webbklipp.",
        },
        {
          title: "Fast pris",
          body: "Offert med fast pris och tidsplan inom 48 timmar efter första samtalet.",
        },
      ]}
      pricing={{
        heading: "Vad kostar tv-produktion 2026?",
        intro:
          "Riktpriser exklusive moms för produktion. Mediakostnad för sändning köps separat.",
        rows: [
          {
            format: "Versionering till tv",
            scope: "Nedklipp av befintlig film, ljudmix, export i kanalformat",
            price: "15 000–40 000 kr",
            lead: "3–5 dagar",
          },
          {
            format: "Tv-reklam 15–30 sek",
            scope: "Koncept, manus, casting, regi, post och versionering",
            price: "150 000–450 000 kr",
            lead: "4–6 veckor",
          },
          {
            format: "Sponsrat inslag / programvinjett",
            scope: "Koncept, inspelning 1 dag, grafikpaket och versioner",
            price: "90 000–250 000 kr",
            lead: "3–5 veckor",
          },
          {
            format: "Seriellt format för play",
            scope: "Flera avsnitt, gemensam grafisk identitet, ljudmix",
            price: "250 000–700 000 kr",
            lead: "6–10 veckor",
          },
        ],
        footnote:
          "Alla priser exklusive moms och exklusive mediaköp. Fler versioner i samma produktion sänker kostnaden per format kraftigt.",
      }}
      process={{
        heading: "Så går en tv-produktion till",
        steps: [
          { label: "Mål och kanalval", body: "Vilka kanaler, vilka längder och vilken målgrupp — det styr allt annat.", duration: "vecka 1" },
          { label: "Koncept och manus", body: "Idé, manus och storyboard med godkännande innan produktion bokas.", duration: "vecka 1–2" },
          { label: "Pre-production", body: "Casting, location, tillstånd och teknikplan för sändningsleverans.", duration: "vecka 2–3" },
          { label: "Inspelning", body: "Regi, foto, ljus och ljud med eget team. Typiskt 1–2 dagar.", duration: "vecka 3–4" },
          { label: "Post och leverans", body: "Klipp, grade, ljudmix mot −23 LUFS, textning och export per kanal.", duration: "vecka 4–6" },
        ],
      }}
      sections={[
        {
          id: "tv-vs-webb",
          heading: "Skillnaden mot vanlig reklamfilm på webben",
          body: (
            <>
              <p>
                Kreativt är det samma hantverk. Tekniskt är det inte det. Tv
                kräver exakta längder, godkända ljudnivåer och specifika
                codec-leveranser per kanal. En film som fungerar perfekt på
                LinkedIn kan underkännas av en sändande kanal på rena
                formalia.
              </p>
              <p>
                Planerar ni både tv och digitalt: låt tv-versionen styra
                inspelningen och klipp de digitala versionerna ur samma
                material. Se{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm">
                  reklamfilm
                </a>{" "}
                för det digitala upplägget.
              </p>
            </>
          ),
        },
        {
          id: "budget",
          heading: "Hur budgeten fördelas",
          body: (
            <p>
              I en typisk tv-reklam går ungefär en tredjedel till
              pre-production och koncept, en tredjedel till inspelningsdagen
              (team, teknik, location, casting) och en tredjedel till post
              (klipp, grade, ljud, grafik, versionering). Mediaköpet ligger
              utanför och är ofta större än produktionsbudgeten. Läs mer i{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/vad-kostar-en-reklamfilm">
                vad kostar en reklamfilm
              </a>
              .
            </p>
          ),
        },
        {
          id: "vem",
          heading: "Vem vi gör tv-produktion för",
          body: (
            <p>
              Varumärken som kompletterar digital närvaro med tv eller play,
              organisationer med kampanjer i public service-miljö och bolag som
              sponsrar program. Behöver ni bara webbfilm är{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foretagsfilm">
                företagsfilm
              </a>{" "}
              oftast rätt väg.
            </p>
          ),
        },
      ]}
      faq={FAQ}
      caseLimit={6}
      riskReducers
      closing={{
        title: "Planerar ni tv eller play?",
        body: "Boka 30 minuter så går vi igenom kanalkrav, längder och vad produktionen kostar.",
        primary: { label: "Boka strategimöte", to: "/kontakt" },
        secondary: { label: "Se priser", to: "/priser" },
      }}
    />
  );
}
